/**
 * Utility functions for geometry calculations
 */

/**
 * Calculate distance between two points in meters using the Haversine formula
 * @param lat1 Latitude of point 1 in degrees
 * @param lng1 Longitude of point 1 in degrees
 * @param lat2 Latitude of point 2 in degrees
 * @param lng2 Longitude of point 2 in degrees
 * @returns Distance in meters
 */
export const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

/**
 * Format distance for display
 * @param meters Distance in meters
 * @returns Formatted distance string (e.g. "230m" or "1.2km")
 */
export const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${Math.round(meters)}`;
  } else {
    return `${(meters / 1000).toFixed(1)}`;
  }
};

/**
 * Find a point along a geometry path that is approximately targetDistance (meters)
 * from the first point, following the path segments.
 * 
 * @param geometry Array of points with lat/lng properties
 * @param targetDistance Target distance in meters from the first point
 * @returns Point at the target distance or the last point if target distance exceeds path length
 */
export const findPointAtDistance = (
  geometry: Array<{ lat: number; lng: number }>,
  targetDistance: number = 230
): { lat: number; lng: number } => {
  if (!geometry || geometry.length < 2) {
    return geometry[0];
  }

  // Start from the first point
  let currentDistance = 0;
  let previousPoint = geometry[0];

  // Go through each segment of the path
  for (let i = 1; i < geometry.length; i++) {
    const currentPoint = geometry[i];

    // Calculate distance of this segment
    const segmentDistance = calculateDistance(
      previousPoint.lat, previousPoint.lng,
      currentPoint.lat, currentPoint.lng
    );

    // Check if adding this segment exceeds our target distance
    if (currentDistance + segmentDistance >= targetDistance) {
      // Calculate how far along this segment we need to go
      const remainingDistance = targetDistance - currentDistance;
      const fraction = segmentDistance > 0 ? remainingDistance / segmentDistance : 0;

      // Interpolate to find the exact point
      const lat = previousPoint.lat + fraction * (currentPoint.lat - previousPoint.lat);
      const lng = previousPoint.lng + fraction * (currentPoint.lng - previousPoint.lng);

      return { lat, lng };
    }

    // Add this segment's distance to our running total
    currentDistance += segmentDistance;
    previousPoint = currentPoint;
  }

  // If we've gone through all segments and haven't reached the target distance,
  // return the last point
  return geometry[geometry.length - 1];
};
